import {toPageEither, toPage, fromResult} from 'Core/models/Async';
import {
  getEnrollmentApplications,
  updateEnrollmentFileAuditStatus,
  EnrollmentApplicationTemplateType,
} from 'Enrollment/ducks/enrollmentFileProcessing';
import {EnrollmentApplication} from 'Enrollment/models/EnrollmentApplication';
import React, {useEffect} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {ReducerState} from 'rootReducer';
import Column from 'Shared/components/PagedView/Column';
import {toFormattedDateTime} from 'Shared/utilities/DateFormatter';
import PagedView from 'Shared/components/PagedView/PagedView';
import {renderAsync} from 'Shared/components/RenderAsync';
import * as E from 'fp-ts/lib/Either';
import {pipe} from 'fp-ts/lib/pipeable';
import ActionsMenu from 'Shared/components/PagedView/ActionsMenu';
import {MenuItem, Toolbar, Typography} from '@material-ui/core';
import {EnrollmentFileStatusType} from 'Enrollment/models/EnrollmentFileStatusType';
import {EnrollmentRouteConstants} from 'Enrollment/Routes';
import {useHistory, useParams} from 'react-router';
import './EnrollmentApplicationFileValidationResults.sass';

const EnrollmentApplicationFileValidationResults = () => {
  const dispatch = useDispatch();
  const {fileId} = useParams();
  const history = useHistory();

  const {enrollmentApplicationsFilters, enrollmentApplications} = useSelector(
    (state: ReducerState) => state.enrollmentFileProcessing
  );

  useEffect(() => {
    dispatch(getEnrollmentApplications(undefined, enrollmentApplicationsFilters));
  }, [dispatch, enrollmentApplicationsFilters]);

  const handleAuditFileClick = (fileId: string) => {
    dispatch(updateEnrollmentFileAuditStatus(fileId));
  };

  const handleAuditDetailsClick = (fileId: string) => {
    history.push(EnrollmentRouteConstants.ENROLLMENT_APPLICATION_FILE_AUDIT_DETAIL(fileId!));
  };

  const applications = toPageEither(enrollmentApplications);

  const getIsFileReadyForAuditing = (file: EnrollmentApplication) => {
    const hasAtLeastOneValidApplication = file.validRecordCount! > 0;
    return (
      EnrollmentFileStatusType.VALIDATION_COMPLETE === file.fileStatus!.id &&
      hasAtLeastOneValidApplication &&
      getIsFileGenelcoSpreadsheetTemplateType(file)
    );
  };

  const getIsFileAuditInProgressOrCompleted = (file: EnrollmentApplication) => {
    return (
      EnrollmentFileStatusType.READY_FOR_AUDIT === file.fileStatus!.id ||
      EnrollmentFileStatusType.AUDIT_COMPLETE === file.fileStatus!.id ||
      EnrollmentFileStatusType.KICKOUT_VALIDATION_COMPLETE === file.fileStatus!.id
    );
  };

  const getIsFileGenelcoSpreadsheetTemplateType = (file: EnrollmentApplication) => {
    return file.templateType?.id === EnrollmentApplicationTemplateType.GenelcoSpreadsheet;
  };

  return pipe(
    applications,
    E.fold(renderAsync, enrollmentApplicationValidationFiles => {
      const enrollmentApplicationValidationFile = enrollmentApplicationValidationFiles.result.filter(
        data => data.id === fileId
      );
      return (
        <div className="enrollment-file-validation-results-container">
          <Toolbar className="enrollment-file-validation-results-container__header">
            <Typography id="enrollment-file-validation-results-title" className="enrollment-file-validation-results-container__header-title" variant="h6">
              Validation Results
            </Typography>
          </Toolbar>

          <PagedView
            pagedViewKey="enrollmentApplicationFileValidationResults"
            data={fromResult(toPage(enrollmentApplicationValidationFile))}
            loadMore={() => {}}
          >
            {columnProps => (
              <>
                <Column {...columnProps} title="File Name" default>
                  {validationResult => validationResult.fileName}
                </Column>
                <Column {...columnProps} title="Date Of Validation" default>
                  {validationResult => toFormattedDateTime(validationResult.fileUploadedDate!)}
                </Column>
                <Column {...columnProps} title="Number Of Records" default>
                  {validationResult => validationResult.rowCount!.toString()}
                </Column>
                <Column {...columnProps} title="Valid Records" default>
                  {validationResult => validationResult.validRecordCount!.toString()}
                </Column>
                <Column {...columnProps} title="Invalid Records" default>
                  {validationResult => validationResult.invalidRecordCount!.toString()}
                </Column>
                <Column {...columnProps} title="Actions" isActions default>
                  {validationResult => (
                    <>
                      <ActionsMenu>
                        <MenuItem
                          id="menu-item-actions-audit-file" 
                          disabled={!getIsFileReadyForAuditing(validationResult)}
                          onClick={() => handleAuditFileClick(validationResult.id!)}
                        >
                          Audit File
                        </MenuItem>
                        <MenuItem
                          id="menu-item-actions-view-audit-detail"
                          disabled={!getIsFileAuditInProgressOrCompleted(validationResult)}
                          onClick={() => handleAuditDetailsClick(validationResult.id!)}
                        >
                          View Audit Detail
                        </MenuItem>
                      </ActionsMenu>
                    </>
                  )}
                </Column>
              </>
            )}
          </PagedView>
        </div>
      );
    })
  );
};

export default EnrollmentApplicationFileValidationResults;
